class Random3DModel extends HTMLElement {
  constructor() {
    super();
    this.model = null;
    this.frame = 0;
    this.lastTime = 0;
    this.dragging = false;
    this.visible = true;
    this.started = false;
    this.orbit = [75, 0];
    this.orientation = [0, 0, 0];
    this.velocity = [0, 0, 0];
    this.targetVelocity = [0, 0, 0];
    this.nextDirectionChange = [0, 0, 0];
    this.pointer = { type: null, id: null, touchId: null, x: 0, y: 0 };
    this.dragDistance = 0;
    this.suppressNextClick = false;
    this.returningToRest = false;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onClick = this.onClick.bind(this);
    this.tick = this.tick.bind(this);
  }

  connectedCallback() {
    this.model = this.querySelector('model-viewer');
    if (!this.model) return;

    this.speed = Math.max(0, Number(this.dataset.speed || 0));
    this.rotationDirection = Number(this.dataset.rotationDirection) < 0 ? -1 : 1;
    this.idleAxis = this.dataset.idleAxis || 'all';
    this.allowClick = this.dataset.allowClick === 'true';
    this.resetOnRelease = this.dataset.resetOnRelease === 'true';
    this.dragSensitivity = Number(this.dataset.dragSensitivity || 0.45);
    this.orbitDistance = `${Number(this.dataset.zoom || 105)}%`;
    this.orbit = [75, Number(this.dataset.cameraAngle || 0)];
    this.orientation = [0, 0, 0];
    this.pickDirections();

    this.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    window.addEventListener('pointermove', this.onPointerMove, { passive: false, capture: true });
    window.addEventListener('pointerup', this.onPointerUp, true);
    window.addEventListener('pointercancel', this.onPointerUp, true);
    this.addEventListener('touchstart', this.onTouchStart, { passive: false, capture: true });
    this.addEventListener('click', this.onClick, true);
    window.addEventListener('touchmove', this.onTouchMove, { passive: false, capture: true });
    window.addEventListener('touchend', this.onTouchEnd, true);
    window.addEventListener('touchcancel', this.onTouchEnd, true);

    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = Boolean(entry?.isIntersecting);
    });
    this.observer.observe(this);

    customElements.whenDefined('model-viewer').then(() => {
      const start = () => {
        if (!this.isConnected || this.started) return;
        this.started = true;
        this.applyRotation();
        this.lastTime = performance.now();
        if (this.speed === 0) return;
        this.frame = requestAnimationFrame(this.tick);
      };

      if (this.model.loaded) {
        start();
      } else {
        this.model.addEventListener('load', start, { once: true });
      }
    });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.started = false;
    this.observer?.disconnect();
    this.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointermove', this.onPointerMove, true);
    window.removeEventListener('pointerup', this.onPointerUp, true);
    window.removeEventListener('pointercancel', this.onPointerUp, true);
    this.removeEventListener('touchstart', this.onTouchStart, true);
    this.removeEventListener('click', this.onClick, true);
    window.removeEventListener('touchmove', this.onTouchMove, true);
    window.removeEventListener('touchend', this.onTouchEnd, true);
    window.removeEventListener('touchcancel', this.onTouchEnd, true);
  }

  random(min, max) {
    return min + Math.random() * (max - min);
  }

  pickAxisDirection(index, time = performance.now()) {
    const speedRanges = [
      [0.35, 0.85],
      [0.4, 1],
      [0.3, 0.9],
    ];
    const durationRanges = [
      [1700, 4300],
      [1400, 3900],
      [1900, 4700],
    ];
    const [minimumSpeed, maximumSpeed] = speedRanges[index];
    const [minimumDuration, maximumDuration] = durationRanges[index];
    const direction = Math.random() > 0.5 ? 1 : -1;

    this.targetVelocity[index] = this.random(minimumSpeed, maximumSpeed) * this.speed * direction;
    this.nextDirectionChange[index] = time + this.random(minimumDuration, maximumDuration);
  }

  pickDirections(time = performance.now()) {
    const idleAxisIndex = { x: 0, y: 1, z: 2 }[this.idleAxis];

    for (let index = 0; index < 3; index += 1) {
      if (idleAxisIndex !== undefined) {
        this.targetVelocity[index] = index === idleAxisIndex ? this.speed * this.rotationDirection : 0;
        this.nextDirectionChange[index] = Number.POSITIVE_INFINITY;
      } else {
        this.pickAxisDirection(index, time);
      }
    }
  }

  applyRotation() {
    const polarAngle = Math.max(8, Math.min(172, this.orbit[0]));
    this.orbit[0] = polarAngle;
    this.model.setAttribute('camera-orbit', `${this.orbit[1]}deg ${polarAngle}deg ${this.orbitDistance}`);
    this.model.setAttribute(
      'orientation',
      `${this.orientation[0]}deg ${this.orientation[1]}deg ${this.orientation[2]}deg`
    );
  }

  onPointerDown(event) {
    if (event.pointerType !== 'touch' && event.button !== 0) return;
    if (!this.allowClick && event.cancelable) event.preventDefault();
    this.dragging = true;
    this.returningToRest = false;
    this.dragDistance = 0;
    this.suppressNextClick = false;
    this.pointer = {
      type: event.pointerType === 'touch' ? 'pointer-touch' : 'pointer',
      id: event.pointerId,
      touchId: null,
      x: event.clientX,
      y: event.clientY,
    };
    this.velocity = [0, 0, 0];
    this.targetVelocity = [0, 0, 0];
    try {
      this.setPointerCapture(event.pointerId);
    } catch (_) {
      // Safari can reject capture while handing the gesture from Touch Events to Pointer Events.
    }
    this.classList.add('is-dragging');
  }

  onPointerMove(event) {
    if (
      !this.dragging ||
      !['pointer', 'pointer-touch'].includes(this.pointer.type) ||
      event.pointerId !== this.pointer.id
    )
      return;

    if (event.cancelable) event.preventDefault();
    this.updateDrag(event.clientX, event.clientY);
  }

  onPointerUp(event) {
    if (
      !this.dragging ||
      !['pointer', 'pointer-touch'].includes(this.pointer.type) ||
      event.pointerId !== this.pointer.id
    )
      return;
    try {
      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    } catch (_) {
      // The pointer may already have been released by Safari.
    }
    this.endDrag();
  }

  onTouchStart(event) {
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    if (!this.allowClick) event.preventDefault();

    if (this.dragging && this.pointer.type === 'pointer-touch') {
      this.pointer.touchId = touch.identifier;
      return;
    }
    if (this.dragging) return;

    this.dragging = true;
    this.returningToRest = false;
    this.dragDistance = 0;
    this.suppressNextClick = false;
    this.pointer = {
      type: 'touch',
      id: null,
      touchId: touch.identifier,
      x: touch.clientX,
      y: touch.clientY,
    };
    this.velocity = [0, 0, 0];
    this.targetVelocity = [0, 0, 0];
    this.classList.add('is-dragging');
  }

  onTouchMove(event) {
    if (!this.dragging || !['touch', 'pointer-touch'].includes(this.pointer.type)) return;
    const touch = Array.from(event.touches).find(
      (item) => this.pointer.touchId === null || item.identifier === this.pointer.touchId
    );
    if (!touch) return;

    event.preventDefault();
    if (this.pointer.touchId === null) this.pointer.touchId = touch.identifier;
    this.updateDrag(touch.clientX, touch.clientY);
  }

  onTouchEnd(event) {
    if (!this.dragging || !['touch', 'pointer-touch'].includes(this.pointer.type)) return;
    const touchStillActive = Array.from(event.touches).some(
      (item) => this.pointer.touchId === null || item.identifier === this.pointer.touchId
    );
    if (touchStillActive) return;
    this.endDrag();
  }

  updateDrag(clientX, clientY) {
    const deltaX = clientX - this.pointer.x;
    const deltaY = clientY - this.pointer.y;
    this.dragDistance += Math.hypot(deltaX, deltaY);
    if (this.dragDistance > 6) this.suppressNextClick = true;
    this.orbit[0] -= deltaY * this.dragSensitivity;
    this.orbit[1] -= deltaX * this.dragSensitivity;
    this.pointer.x = clientX;
    this.pointer.y = clientY;
    this.applyRotation();
  }

  onClick(event) {
    if (!this.allowClick || !this.suppressNextClick) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.suppressNextClick = false;
  }

  endDrag() {
    const shouldReset = this.resetOnRelease && this.suppressNextClick;
    this.dragging = false;
    this.pointer = { type: null, id: null, touchId: null, x: 0, y: 0 };
    this.classList.remove('is-dragging');

    if (shouldReset) {
      this.returningToRest = true;
      this.velocity[0] = 0;
      this.velocity[1] = 0;
    }

    this.pickDirections();
  }

  tick(time) {
    const delta = Math.min((time - this.lastTime) / 1000, 0.05);
    this.lastTime = time;

    if (this.visible && !this.dragging && !this.reduceMotion.matches) {
      if (this.returningToRest) {
        const restEasing = 1 - Math.exp(-delta * 5.5);
        const restingOrbit = [75, Number(this.dataset.cameraAngle || 0)];

        this.orbit[0] += (restingOrbit[0] - this.orbit[0]) * restEasing;
        this.orbit[1] += (restingOrbit[1] - this.orbit[1]) * restEasing;
        this.orientation[0] += (0 - this.orientation[0]) * restEasing;
        this.orientation[1] += (0 - this.orientation[1]) * restEasing;

        const isAtRest =
          Math.abs(this.orbit[0] - restingOrbit[0]) < 0.05 &&
          Math.abs(this.orbit[1] - restingOrbit[1]) < 0.05 &&
          Math.abs(this.orientation[0]) < 0.05 &&
          Math.abs(this.orientation[1]) < 0.05;

        if (isAtRest) {
          this.orbit = restingOrbit;
          this.orientation[0] = 0;
          this.orientation[1] = 0;
          this.returningToRest = false;
        }
      }

      const easing = 1 - Math.exp(-delta * 0.85);
      for (let index = 0; index < 3; index += 1) {
        if (time >= this.nextDirectionChange[index]) this.pickAxisDirection(index, time);
        this.velocity[index] += (this.targetVelocity[index] - this.velocity[index]) * easing;
        this.orientation[index] += this.velocity[index] * delta;
        if (Math.abs(this.orientation[index]) > 3600) this.orientation[index] %= 360;
      }
      this.applyRotation();
    }

    this.frame = requestAnimationFrame(this.tick);
  }
}

if (!customElements.get('random-3d-model')) {
  customElements.define('random-3d-model', Random3DModel);
}
